import {
  BaseButton,
  Button,
  DefaultButton,
  IBasePicker,
  IStackTokens,
  ITag,
  Image,
  Label,
  PrimaryButton,
  Stack,
  TagPicker,
  Text,
  ValidationState,
} from "@fluentui/react";
import { useBoolean } from "@fluentui/react-hooks";
import React, { useEffect, useRef, useState } from "react";
import CurrencyInput from "react-currency-input-field";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { AddItemFormInput, addItemFormsDefault, menuItemDbToFormData } from "../../common/draftMenuHelper";
import { addItemToBank } from "../../reducers/diningReducer";
import {
  cafeAllergenBankSelector,
  cafeSelector,
  currencySelector,
  selectedItemSelector,
  stationSelector,
  weekStartStringSelector,
} from "../../selectors/diningSelector";
import styles from "../../styles/addItem.module.css";
import withAppInsights from "../AppInsights/AppInsights";
import { ImageCropForm } from "./ImageCropForm";
export interface IMenuItemImage {
  file: File;
  imagePreviewUrl: string;
}

export interface AddItemFormProps {
  closePanel: () => void;
}

const AddItemForm = ({ closePanel }: AddItemFormProps, isEditMode: Boolean) => {
  const selectedStation = useSelector(stationSelector);
  const selectedCafe = useSelector(cafeSelector);
  const currentWeek = useSelector(weekStartStringSelector);
  const selectedItem = useSelector(selectedItemSelector);
  const item = selectedItem ? menuItemDbToFormData(selectedItem) : addItemFormsDefault;
  const allergenBank = useSelector(cafeAllergenBankSelector);
  const currentCurrency = useSelector(currencySelector);
  const dispatch = useDispatch();

  const { register, handleSubmit, control, errors, setValue, getValues, watch } = useForm<AddItemFormInput>({
    mode: "onChange",
  });
  const selectedAllergens = watch("allergens", item.allergens);
  let optionalAllergensInputRef = useRef<IBasePicker<ITag> | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const [cropComplete, { setTrue: setCropComplete, setFalse: resetCropComplete }] = useBoolean(false);
  const [croppedMenuItemImage, setCroppedMenuItemImage] = useState<IMenuItemImage>();
  const [isModalOpen, { setTrue: openModal, setFalse: closeModal }] = useBoolean(false);
  const [deleteImageButton, { setTrue: showDeleteImageButton, setFalse: hideDeleteImageButton }] = useBoolean(false);

  useEffect(() => {
    if (item.image?.imageUrl) {
      setValue("image", { previewUrl: item.image?.imageUrl, imageUrl: item.image?.imageUrl }, { shouldDirty: false });
      showDeleteImageButton();
    } else {
      hideDeleteImageButton();
    }
  }, []);

  useEffect(() => {
    if (cropComplete && croppedMenuItemImage) {
      const image = getValues("image");
      if (image && image.previewUrl) {
        URL.revokeObjectURL(image.previewUrl);
      }
      const file = croppedMenuItemImage.file;
      const previewUrl = URL.createObjectURL(file);
      setValue("image", { file: file, previewUrl: previewUrl, imageUrl: null }, { shouldDirty: true });
      showDeleteImageButton();
    }
  }, [cropComplete, croppedMenuItemImage]);

  const resetImageRefAndState = () => {
    resetCropComplete();
    setCroppedMenuItemImage(undefined);
    clearImage();
    hideDeleteImageButton();
  };

  const onBrowseClick = (
    e: React.MouseEvent<
      HTMLAnchorElement | HTMLButtonElement | HTMLDivElement | HTMLSpanElement | BaseButton | Button,
      MouseEvent
    >,
  ) => {
    if (imageInputRef.current) {
      imageInputRef.current.click();
    }
  };

  const onImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (cropComplete && croppedMenuItemImage) {
      resetImageRefAndState();
    }
    if (e.target.files && e.target.files.length > 0) {
      setValue(
        "image",
        { file: e.target.files[0], previewUrl: URL.createObjectURL(e.target.files[0]), imageUrl: null },
        { shouldDirty: true },
      );
      openModal();
    }
  };

  const clearImage = (
    e?: React.MouseEvent<
      HTMLAnchorElement | HTMLButtonElement | HTMLDivElement | HTMLSpanElement | BaseButton | Button,
      MouseEvent
    >,
  ) => {
    const itemImage = getValues("image");
    if (itemImage && itemImage.previewUrl) {
      URL.revokeObjectURL(itemImage.previewUrl);
    }
    setValue("image", addItemFormsDefault.image);
    if (imageInputRef.current) {
      imageInputRef.current.value = "";
    }
  };

  const onSubmit = (data: AddItemFormInput) => {
    if (!selectedCafe) {
      throw new Error("Cafe should be selected.");
    }
    if (!selectedStation) {
      throw new Error("Station should be selected.");
    }
    data.id ? null : (data.id = uuidv4());
    let isEditedItem = selectedItem ? true : false;
    dispatch(addItemToBank(selectedCafe.id, selectedStation.id, data, currentWeek, isEditedItem));
    closePanel();
  };

  const mainStackToken: IStackTokens = {
    childrenGap: 15,
  };

  const horizontalStackToken: IStackTokens = { childrenGap: 5 };

  const onImageDelete = () => {
    if (cropComplete && croppedMenuItemImage) {
      resetImageRefAndState();
      return;
    }
    const itemImage = getValues("image");
    setValue(
      "image",
      {
        deleteUrl: itemImage?.imageUrl,
        previewUrl: null,
        imageUrl: null,
      },
      { shouldDirty: true },
    );
    hideDeleteImageButton();
  };

  register("image");

  // Allergen (Tag) picker helper functions

  // Converts Allergens into Tags. Needed for "TagPicker" typing. Otherwise we'd need to make a custom component.
  let allergenList: ITag[] = [];
  allergenBank?.forEach((a) => a.name && a.id && allergenList.push({ name: a.name, key: a.id } as ITag));

  // When a user types in a string that isn't in the allergenList, we add it in.
  // Cons: user taint from bad strings.
  // Pros: most allergen banks are empty so we need to account for missing allergens.
  const addToAllergenList = (newWord: string) => {
    const newAllergen: ITag = {
      name: newWord,
      key: uuidv4(),
    };
    allergenList.push(newAllergen);
    return newAllergen;
  };

  // Checks to see if the passed tag is in the allergenList
  const listContainsTagList = (tag: ITag, tagList?: ITag[]) => {
    if (!tagList || !tagList.length || tagList.length === 0) {
      return false;
    }
    return tagList.some((compareTag) => compareTag.key === tag.key);
  };

  // Returns a list of allergens that contain the current text input
  const _onResolveSuggestions = (filterText: string) => {
    const suggestionsFromBank = allergenList
      ?.filter((a) => a.name?.toLowerCase().startsWith(filterText.toLowerCase()))
      .map(({ name }) => ({
        key: name,
        name,
      }));
    if (suggestionsFromBank && suggestionsFromBank.length) {
      return suggestionsFromBank;
    } else {
      // Return free form text in title case. This is done to remove chances of
      // "peanut" vs "Peanut" during bank creation.
      const titleCaseSuggestion = filterText
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.substr(1).toLowerCase())
        .join(" ");
      return [
        {
          key: uuidv4(),
          name: titleCaseSuggestion,
        },
      ];
    }
  };

  // This fills the same job as _onResolveSuggestions, but the optionalAllergen list can only include allergens
  // that have already been selected in the allergen field.
  const filterOptionalSuggestedTags = (filterText: string, tagList: ITag[]): ITag[] => {
    return filterText && selectedAllergens.length > 0
      ? selectedAllergens.filter(
          (tag) =>
            tag.name?.toLowerCase().indexOf(filterText.toLowerCase()) === 0 && !listContainsTagList(tag, tagList),
        )
      : [];
  };
  const getTextFromItem = (item: ITag) => item.name;

  return (
    // NOTE: 'message' fields have no defined text in the Figma mocks. We'll make correct warnings when the time comes.
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack tokens={mainStackToken} verticalFill={true}>
        <Stack>
          <input hidden readOnly value={item.id} name={"id"} ref={register()} />
          <Label>
            Item name <span className={styles.required}>*</span>
          </Label>
          <input
            className={errors?.name?.message ? styles.textInputBoxError : styles.textInputBox}
            name={"name"}
            placeholder={"Menu Item Name"}
            defaultValue={item.name}
            aria-label={"Menu item name, required"}
            aria-invalid={errors.name ? "true" : "false"}
            ref={register({
              required: { value: true, message: "Required field." },
            })}
            data-testid="ItemNameInput"
          />
          {errors?.name?.message && (
            <Text role={"alert"} className={styles.errorMessage}>
              {errors?.name?.message}
            </Text>
          )}
        </Stack>
        <Stack>
          <Label>
            Item description <span className={styles.required}>*</span>
          </Label>
          <textarea
            className={errors?.description?.message ? styles.textInputBoxError : styles.textInputBox}
            name={"description"}
            placeholder={"Add your description"}
            defaultValue={item.description}
            aria-label={"Menu item description, required"}
            aria-invalid={errors.description ? "true" : "false"}
            ref={register({
              required: { value: true, message: "Required field." },
            })}
            data-testid="ItemDescriptionTextArea"
          />
          {errors?.description?.message && (
            <Text role={"alert"} className={styles.errorMessage}>
              {errors?.description?.message}
            </Text>
          )}
        </Stack>
        <Stack>
          <Label>Category</Label>
          <input
            name={"category"}
            className={styles.textInputBox}
            ref={register}
            aria-label={"Category"}
            data-testid="ItemCategoryTextArea"
            defaultValue={item.category}
          />
        </Stack>
        <Stack horizontalAlign="space-between" horizontal={true} wrap={false}>
          <Stack.Item className={styles.panelHalfWidth} grow={false}>
            <Label>Price:</Label>
            <Controller
              control={control}
              name={"price"}
              defaultValue={item.price}
              rules={{
                required: { value: true, message: "Please enter a valid price." },
              }}
              render={({ onChange, value, name }) => (
                <CurrencyInput
                  name={name}
                  placeholder={"Price"}
                  defaultValue={item.price}
                  onChange={onChange}
                  allowNegativeValue={false}
                  intlConfig={{ locale: "en-US", currency: currentCurrency.code }}
                  data-testid={"ItemPriceInput"}
                  className={styles.priceInput}
                  decimalsLimit={10}
                />
              )}
            />
            {errors?.price?.message && (
              <Text role={"alert"} className={styles.errorMessage}>
                {errors?.price?.message}
              </Text>
            )}
          </Stack.Item>
          <Stack.Item className={styles.panelHalfWidth}>
            <Label>Calories: </Label>
            <input
              name={"calories"}
              className={errors?.calories?.message ? styles.textInputBoxError : styles.textInputBox}
              defaultValue={item.calories}
              aria-label={"Number of calories, optional."}
              aria-invalid={errors.calories ? "true" : "false"}
              data-testid={"ItemCaloriesInput"}
              placeholder={"Unknown"}
              ref={register({
                pattern: {
                  value: /^[1-9]{0,1}[0-9]{0,5}$/,
                  message: "Positive values only. Leave blank if unknown.",
                },
              })}
            />

            {errors?.calories?.message && (
              <Text role={"alert"} className={styles.errorMessage}>
                {errors?.calories?.message}
              </Text>
            )}
          </Stack.Item>
        </Stack>
        <Stack>
          <Label htmlFor="containsAllergensPicker">Contains allergens:</Label>
          <Stack
            className={styles.allergenStack}
            wrap
            verticalFill={false}
            tokens={{ childrenGap: 15, maxHeight: 100 }}
          >
            <Stack.Item key={"containsAllergens"}>
              <Controller
                key={"allergens"}
                name={"allergens"}
                control={control}
                defaultValue={item.allergens}
                render={({ onChange }) => (
                  <TagPicker
                    defaultSelectedItems={item.allergens}
                    className={styles.allergenStackPicker}
                    removeButtonAriaLabel="Remove"
                    onChange={onChange}
                    aria-label="Includes the following allergens:"
                    onResolveSuggestions={_onResolveSuggestions}
                    getTextFromItem={(a: ITag) => a.name}
                    componentRef={register}
                    createGenericItem={addToAllergenList}
                    pickerSuggestionsProps={{
                      suggestionsHeaderText: "Matching allergens",
                      noResultsFoundText: "No allergens found",
                    }}
                    itemLimit={100}
                    inputProps={
                      {
                        "data-testid": "ItemAllergensContain",
                        id: "containsAllergensPicker",
                      } as any
                    }
                    onValidateInput={(input: string) => {
                      return input ? ValidationState.valid : ValidationState.invalid;
                    }}
                  />
                )}
              />
            </Stack.Item>
          </Stack>
        </Stack>
        <Controller
          key={"optionalAllergens"}
          name={"optionalAllergens"}
          control={control}
          defaultValue={item.optionalAllergens}
          render={({ onChange, value }) => (
            <Stack>
              <Label htmlFor="optionalAllergensPicker">Can be made without:</Label>
              <Stack className={styles.allergenStack} wrap tokens={{ childrenGap: 15, maxHeight: 100 }}>
                <Stack.Item key={"optionalAllergens"}>
                  <TagPicker
                    defaultSelectedItems={item.optionalAllergens}
                    removeButtonAriaLabel="Remove allergen"
                    onResolveSuggestions={filterOptionalSuggestedTags}
                    onChange={onChange}
                    getTextFromItem={getTextFromItem}
                    componentRef={(ref) => {
                      optionalAllergensInputRef.current = ref;
                      register;
                    }}
                    pickerSuggestionsProps={{
                      suggestionsHeaderText: "Suggested allergens",
                      noResultsFoundText: "No allergens found",
                    }}
                    inputProps={
                      {
                        "data-testid": "ItemAllergensOptional",
                        id: "optionalAllergensPicker",
                      } as any
                    }
                    disabled={selectedAllergens.length === 0 && item.optionalAllergens.length === 0}
                  />
                </Stack.Item>
              </Stack>
            </Stack>
          )}
        />
        <Stack tokens={mainStackToken}>
          <Label className={styles.noPadding}>Add an Image</Label>
          <Stack className={styles.padTop} horizontal={false} verticalAlign={"start"} tokens={mainStackToken}>
            <Stack>
              <Image width={238} src={getValues("image")?.previewUrl || ""} />
              <ImageCropForm
                isModalOpen={isModalOpen}
                closeModal={closeModal}
                control={control}
                cropComplete={cropComplete}
                setCropImage={setCroppedMenuItemImage}
                setCropComplete={setCropComplete}
                onCancel={clearImage}
              />
            </Stack>
            <Stack horizontal={true} horizontalAlign={"space-between"} tokens={horizontalStackToken}>
              <DefaultButton text={"Browse"} onClick={(e) => onBrowseClick(e)} />
              {deleteImageButton && <DefaultButton text={"Delete"} onClick={(e) => onImageDelete()} />}
              <input
                className={styles.hidden}
                type={"file"}
                accept={"image/jpg, image/jpeg, image/png"}
                ref={(ref) => (imageInputRef.current = ref)}
                onChange={(e) => {
                  onImageChange(e);
                }}
              />
            </Stack>
          </Stack>
        </Stack>
        <Stack horizontal={true} horizontalAlign={"end"} tokens={mainStackToken}>
          <PrimaryButton
            text={selectedItem ? "Save" : "Add to menu bank"}
            type={"submit"}
            data-testid={"AddSubmitBtn"}
          />
        </Stack>
      </Stack>
    </form>
  );
};

export default withAppInsights(AddItemForm);
